import type { MembershipRole } from '@services/auth.service';

/** Mirrors backend membership-role.constants.ts */
export const MEMBERSHIP_OPS_WRITE: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
];

export const MEMBERSHIP_OPS_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
  'APPROVER',
  'VIEWER',
];

export const MEMBERSHIP_FLEET_WRITE: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
];

export const MEMBERSHIP_FLEET_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
  'VIEWER',
];

export const MEMBERSHIP_ADMIN_WRITE: MembershipRole[] = ['OWNER', 'ADMIN'];

export const MEMBERSHIP_BILLING_WRITE: MembershipRole[] = ['OWNER', 'ADMIN', 'ACCOUNTING'];

export const MEMBERSHIP_BILLING_READ: MembershipRole[] = ['OWNER', 'ADMIN', 'ACCOUNTING'];

export const MEMBERSHIP_DOCKET_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
  'APPROVER',
  'ACCOUNTING',
  'VIEWER',
];

export const MEMBERSHIP_APPROVAL_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'APPROVER',
];

/** Approve/reject/request-changes on MDR approvals. */
export const MEMBERSHIP_APPROVAL_WRITE: MembershipRole[] = ['OWNER', 'ADMIN', 'APPROVER'];

export const MEMBERSHIP_POD_UPLOAD: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
  'DRIVER',
];

export const MEMBERSHIP_POD_VERIFY: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
];

export const MEMBERSHIP_TRIP_EXECUTE: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'DISPATCHER',
  'DRIVER',
];

export const MEMBERSHIP_DOCKET_SETTINGS_WRITE: MembershipRole[] = [
  ...MEMBERSHIP_ADMIN_WRITE,
  'OPERATIONS_MANAGER',
];

export const MEMBERSHIP_COMMERCIAL_OVERRIDE: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
];

export const MEMBERSHIP_COMMERCIAL_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'ACCOUNTING',
  'VIEWER',
];

export const MEMBERSHIP_ENTERPRISE_READ: MembershipRole[] = [
  'OWNER',
  'ADMIN',
  'OPERATIONS_MANAGER',
  'ACCOUNTING',
  'VIEWER',
];

export const MEMBERSHIP_AUDIT_READ: MembershipRole[] = ['OWNER', 'ADMIN'];

export const MEMBERSHIP_MANAGE_MEMBERS: MembershipRole[] = MEMBERSHIP_ADMIN_WRITE;

export type PermissionAction =
  | 'opsWrite'
  | 'opsRead'
  | 'fleetWrite'
  | 'fleetRead'
  | 'podUpload'
  | 'podVerify'
  | 'adminWrite'
  | 'billingWrite'
  | 'billingRead'
  | 'docketSettingsWrite'
  | 'manageMembers'
  | 'commercialOverride'
  | 'commercialRead'
  | 'viewAnalytics'
  | 'viewBilling'
  | 'auditRead'
  | 'tripExecute'
  | 'approvalWrite';

const ACTION_ROLES: Record<PermissionAction, MembershipRole[] | 'all'> = {
  opsWrite: MEMBERSHIP_OPS_WRITE,
  opsRead: MEMBERSHIP_OPS_READ,
  fleetWrite: MEMBERSHIP_FLEET_WRITE,
  fleetRead: MEMBERSHIP_FLEET_READ,
  podUpload: MEMBERSHIP_POD_UPLOAD,
  podVerify: MEMBERSHIP_POD_VERIFY,
  adminWrite: MEMBERSHIP_ADMIN_WRITE,
  billingWrite: MEMBERSHIP_BILLING_WRITE,
  billingRead: MEMBERSHIP_BILLING_READ,
  docketSettingsWrite: MEMBERSHIP_DOCKET_SETTINGS_WRITE,
  manageMembers: MEMBERSHIP_MANAGE_MEMBERS,
  commercialOverride: MEMBERSHIP_COMMERCIAL_OVERRIDE,
  commercialRead: MEMBERSHIP_COMMERCIAL_READ,
  viewAnalytics: MEMBERSHIP_COMMERCIAL_READ,
  viewBilling: MEMBERSHIP_BILLING_READ,
  auditRead: MEMBERSHIP_AUDIT_READ,
  tripExecute: MEMBERSHIP_TRIP_EXECUTE,
  approvalWrite: MEMBERSHIP_APPROVAL_WRITE,
};

export function hasMembershipRole(
  role: MembershipRole | null | undefined,
  allowed: MembershipRole[],
): boolean {
  return !!role && allowed.includes(role);
}

export function canPerform(
  role: MembershipRole | null | undefined,
  action: PermissionAction,
): boolean {
  const allowed = ACTION_ROLES[action];
  if (allowed === 'all') return !!role;
  return hasMembershipRole(role, allowed);
}

export function canOpsWrite(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'opsWrite');
}

export function canFleetWrite(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'fleetWrite');
}

export function canPodUpload(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'podUpload');
}

export function canPodVerify(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'podVerify');
}

export function canAdminWrite(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'adminWrite');
}

export function canBillingWrite(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'billingWrite');
}

export function canManageMembers(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'manageMembers');
}

export function canManageDocketSettings(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'docketSettingsWrite');
}

export function canCommercialOverride(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'commercialOverride');
}

export function canCommercialRead(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'commercialRead');
}

export function canAuditRead(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'auditRead');
}

export function canApprovalWrite(role: MembershipRole | null | undefined): boolean {
  return canPerform(role, 'approvalWrite');
}

export function isReadOnlyMembership(role: MembershipRole | null | undefined): boolean {
  return role === 'VIEWER' || role === 'DRIVER';
}
